import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, MapPin, Clock, User, Building2, 
  ChevronRight, AlertCircle, Star, MessageSquare,
  FileText, Save, CreditCard, KeyRound, Eye, EyeOff, Heart, Bell, Trash2, Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cities, getDistricts } from '@/data/turkeyData';
import ProfileSettingsClean from '@/components/ProfileSettingsClean';

import ProfileSettings from '@/components/ProfileSettings';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [slotWatchers, setSlotWatchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [userReviews, setUserReviews] = useState({});
  const [activeTab, setActiveTab] = useState('bookings');
  
  // Billing state
  const [billingInfo, setBillingInfo] = useState({
    billing_type: 'individual',
    company_name: '',
    tax_office: '',
    tax_number: '',
    billing_address: '',
    billing_city: '',
    billing_district: '',
    postal_code: ''
  });
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchUserReviews();
    fetchBillingInfo();
    fetchFavorites();
    fetchSlotWatchers();
  }, []);

  useEffect(() => {
    if (billingInfo.billing_city) {
      setAvailableDistricts(getDistricts(billingInfo.billing_city));
    } else {
      setAvailableDistricts([]);
    }
  }, [billingInfo.billing_city]);

  const fetchBillingInfo = async () => {
    setBillingLoading(true);
    try {
      const response = await axios.get(`${API}/user/billing`, {
        withCredentials: true
      });
      if (response.data.billing_info) {
        setBillingInfo(prev => ({
          ...prev,
          ...response.data.billing_info
        }));
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const saveBillingInfo = async () => {
    setBillingSaving(true);
    try {
      await axios.put(`${API}/user/billing`, billingInfo, {
        withCredentials: true
      });
      toast.success('Fatura bilgileri kaydedildi');
    } catch (error) {
      console.error('Error saving billing info:', error);
      toast.error('Fatura bilgileri kaydedilemedi');
    } finally {
      setBillingSaving(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        withCredentials: true
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/user/favorites`, {
        withCredentials: true
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchSlotWatchers = async () => {
    try {
      const response = await axios.get(`${API}/user/slot-watchers`, {
        withCredentials: true
      });
      setSlotWatchers(response.data);
    } catch (error) {
      console.error('Error fetching slot watchers:', error);
    }
  };

  const removeFavorite = async (facilityId) => {
    try {
      await axios.delete(`${API}/user/favorites/${facilityId}`, {
        withCredentials: true
      });
      setFavorites(prev => prev.filter(f => f.facility_id !== facilityId));
      toast.success('Favorilerden kaldırıldı');
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const removeSlotWatcher = async (watcherId) => {
    try {
      await axios.delete(`${API}/user/slot-watchers/${watcherId}`, {
        withCredentials: true
      });
      setSlotWatchers(prev => prev.filter(w => w.watcher_id !== watcherId));
      toast.success('Takip kaldırıldı');
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const fetchUserReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/user`, {
        withCredentials: true
      });
      // Create a map of booking_id to review
      const reviewsMap = {};
      response.data.forEach(review => {
        reviewsMap[review.booking_id] = review;
      });
      setUserReviews(reviewsMap);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const openReviewDialog = (booking) => {
    setSelectedBookingForReview(booking);
    setReviewRating(5);
    setReviewComment('');
    setShowReviewDialog(true);
  };

  const submitReview = async () => {
    if (!selectedBookingForReview) return;
    
    if (reviewComment.trim().length < 10) {
      toast.error('Lütfen en az 10 karakter yorum yazın');
      return;
    }

    setReviewLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        facility_id: selectedBookingForReview.facility_id,
        booking_id: selectedBookingForReview.booking_id,
        rating: reviewRating,
        comment: reviewComment
      }, {
        withCredentials: true
      });
      toast.success('Yorumunuz kaydedildi!');
      setShowReviewDialog(false);
      fetchUserReviews();
    } catch (error) {
      console.error('Review error:', error);
      toast.error(error.response?.data?.detail || 'Yorum gönderilemedi');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== newPasswordConfirm) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        withCredentials: true
      });
      toast.success('Şifreniz başarıyla değiştirildi');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleBecomeOperator = async () => {
    setRoleLoading(true);
    try {
      await updateRole('operator');
      toast.success('İşletmeci olarak kaydınız tamamlandı!');
      navigate('/isletmeci');
    } catch (error) {
      console.error('Role update error:', error);
      toast.error('İşlem başarısız oldu');
    } finally {
      setRoleLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`, {}, {
        withCredentials: true
      });
      toast.success('Rezervasyon iptal edildi');
      fetchBookings();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('İptal işlemi başarısız');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING_PAYMENT':
        return <Badge className="status-pending">Ödeme Bekliyor</Badge>;
      case 'CONFIRMED':
        return <Badge className="status-confirmed">Onaylandı</Badge>;
      case 'CANCELLED':
        return <Badge className="status-cancelled">İptal Edildi</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const upcomingBookings = bookings.filter(b => 
    b.payment_status !== 'CANCELLED' && new Date(b.date) >= new Date(new Date().setHours(0,0,0,0))
  );
  const pastBookings = bookings.filter(b => 
    b.payment_status !== 'CANCELLED' && new Date(b.date) < new Date(new Date().setHours(0,0,0,0))
  );
  const cancelledBookings = bookings.filter(b => b.payment_status === 'CANCELLED');

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="user-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-16 h-16 rounded-full border-4 border-white shadow"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-8 h-8 text-emerald-700" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Merhaba, {user?.name}
              </h1>
              <p className="text-slate-600">{user?.email}</p>
            </div>
          </div>

          {user?.role === 'user' && (
            <Button 
              onClick={handleBecomeOperator}
              disabled={roleLoading}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="become-operator-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {roleLoading ? 'İşleniyor...' : 'İşletmeci Ol'}
            </Button>
          )}
          
          {user?.auth_type === 'email' && (
            <Button 
              variant="outline"
              onClick={() => setShowPasswordDialog(true)}
              className="border-slate-300"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Şifre Değiştir
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Aktif Rezervasyon</p>
                  <p className="text-3xl font-bold text-slate-900">{upcomingBookings.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Tamamlanan</p>
                  <p className="text-3xl font-bold text-slate-900">{pastBookings.length}</p>
                </div>
                <Clock className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">İptal Edilen</p>
                  <p className="text-3xl font-bold text-slate-900">{cancelledBookings.length}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border w-full justify-start">
            <TabsTrigger value="bookings">
              <Calendar className="w-4 h-4 mr-2" />
              Rezervasyonlarım
            </TabsTrigger>
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              Profil Bilgileri
            </TabsTrigger>
            <TabsTrigger value="favorites">
              <Heart className="w-4 h-4 mr-2" />
              Favorilerim
            </TabsTrigger>
            <TabsTrigger value="watchers">
              <Bell className="w-4 h-4 mr-2" />
              Takip Ettiklerim
            </TabsTrigger>
            <TabsTrigger value="billing">
              <FileText className="w-4 h-4 mr-2" />
              Fatura Bilgileri
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <ProfileSettingsClean 
              user={user} 
              onUpdate={(updatedUser) => {
                setUser(updatedUser);
                // Refresh to update all data
                setTimeout(() => window.location.reload(), 500);
              }}
            />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Rezervasyonlarım
                  <Badge variant="secondary">{bookings.length} Rezervasyon</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => (
                  <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : bookings.length > 0 ? (
              <Tabs defaultValue="upcoming">
                <TabsList className="mb-4">
                  <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                    Yaklaşan ({upcomingBookings.length})
                  </TabsTrigger>
                  <TabsTrigger value="past" data-testid="tab-past">
                    Geçmiş ({pastBookings.length})
                  </TabsTrigger>
                  <TabsTrigger value="cancelled" data-testid="tab-cancelled">
                    İptal ({cancelledBookings.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upcoming">
                  <BookingList 
                    bookings={upcomingBookings} 
                    onCancel={handleCancelBooking}
                    navigate={navigate}
                    getStatusBadge={getStatusBadge}
                    showCancel
                    userReviews={userReviews}
                    onReview={openReviewDialog}
                    showReview
                  />
                </TabsContent>

                <TabsContent value="past">
                  <BookingList 
                    bookings={pastBookings} 
                    navigate={navigate}
                    getStatusBadge={getStatusBadge}
                    userReviews={userReviews}
                    onReview={openReviewDialog}
                    showReview
                  />
                </TabsContent>

                <TabsContent value="cancelled">
                  <BookingList 
                    bookings={cancelledBookings} 
                    navigate={navigate}
                    getStatusBadge={getStatusBadge}
                    userReviews={userReviews}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="empty-state" data-testid="no-bookings">
                <Calendar className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Henüz rezervasyonunuz yok
                </h3>
                <p className="text-slate-600 mb-4">
                  Hemen bir tesis bulup rezervasyon yapın!
                </p>
                <Button 
                  onClick={() => navigate('/tesisler')}
                  className="bg-emerald-700 hover:bg-emerald-800"
                >
                  Tesis Ara
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Favorilerim */}
              <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Favorilerim ({favorites.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {favorites.length > 0 ? (
                <div className="space-y-3">
                  {favorites.map(facility => (
                    <div 
                      key={facility.facility_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/tesis/${facility.facility_id}`)}
                    >
                      <div className="flex items-center gap-3">
                        {facility.images?.[0] ? (
                          <img 
                            src={facility.images[0]} 
                            alt={facility.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{facility.name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {facility.district}, {facility.city}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-700 font-semibold text-sm">
                          {facility.price_per_hour}₺
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFavorite(facility.facility_id);
                          }}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Henüz favori tesisiniz yok</p>
                  <Button 
                    variant="link" 
                    onClick={() => navigate('/tesisler')}
                    className="text-emerald-600"
                  >
                    Tesisleri Keşfet
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Slot Takipleri */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                İptal Takipleri ({slotWatchers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {slotWatchers.length > 0 ? (
                <div className="space-y-3">
                  {slotWatchers.map(watcher => (
                    <div 
                      key={watcher.watcher_id}
                      className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{watcher.facility_name}</p>
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          {watcher.date}
                          <Clock className="w-3 h-3 ml-2" />
                          {watcher.hour}:00
                        </p>
                      </div>
                      <button
                        onClick={() => removeSlotWatcher(watcher.watcher_id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aktif takibiniz yok</p>
                  <p className="text-xs mt-1">Dolu bir saat için "İptal olursa bildir" seçeneğini kullanın</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* Watchers Tab (combined with favorites above) */}
          <TabsContent value="watchers">
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Takip ettikleriniz yukarıda görünür</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Fatura Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingLoading ? (
              <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <div className="space-y-6">
                {/* Fatura Tipi */}
                <div>
                  <Label>Fatura Tipi</Label>
                  <Select 
                    value={billingInfo.billing_type} 
                    onValueChange={(v) => setBillingInfo({...billingInfo, billing_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Bireysel</SelectItem>
                      <SelectItem value="corporate">Kurumsal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Kurumsal Bilgiler */}
                {billingInfo.billing_type === 'corporate' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label>Şirket/Firma Adı</Label>
                      <Input
                        value={billingInfo.company_name || ''}
                        onChange={(e) => setBillingInfo({...billingInfo, company_name: e.target.value})}
                        placeholder="Şirket adı"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Vergi Dairesi</Label>
                      <Input
                        value={billingInfo.tax_office || ''}
                        onChange={(e) => setBillingInfo({...billingInfo, tax_office: e.target.value})}
                        placeholder="Vergi dairesi"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Vergi Numarası</Label>
                      <Input
                        value={billingInfo.tax_number || ''}
                        onChange={(e) => setBillingInfo({...billingInfo, tax_number: e.target.value})}
                        placeholder="Vergi numarası"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Bireysel TC Kimlik */}
                {billingInfo.billing_type === 'individual' && (
                  <div>
                    <Label>TC Kimlik Numarası</Label>
                    <Input
                      value={billingInfo.tax_number || ''}
                      onChange={(e) => setBillingInfo({...billingInfo, tax_number: e.target.value})}
                      placeholder="TC Kimlik numarası (opsiyonel)"
                      maxLength={11}
                      className="mt-1"
                    />
                  </div>
                )}

                {/* Adres Bilgileri */}
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-900">Fatura Adresi</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Şehir</Label>
                      <Select 
                        value={billingInfo.billing_city || ''} 
                        onValueChange={(v) => setBillingInfo({...billingInfo, billing_city: v, billing_district: ''})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Şehir seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>İlçe</Label>
                      <Select 
                        value={billingInfo.billing_district || ''} 
                        onValueChange={(v) => setBillingInfo({...billingInfo, billing_district: v})}
                        disabled={!billingInfo.billing_city}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={billingInfo.billing_city ? "İlçe seçin" : "Önce şehir seçin"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDistricts.map(district => (
                            <SelectItem key={district} value={district}>{district}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Açık Adres</Label>
                    <Textarea
                      value={billingInfo.billing_address || ''}
                      onChange={(e) => setBillingInfo({...billingInfo, billing_address: e.target.value})}
                      placeholder="Mahalle, sokak, bina no, daire no..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div className="w-32">
                    <Label>Posta Kodu</Label>
                    <Input
                      value={billingInfo.postal_code || ''}
                      onChange={(e) => setBillingInfo({...billingInfo, postal_code: e.target.value})}
                      placeholder="34000"
                      maxLength={5}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Kaydet Butonu */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={saveBillingInfo}
                    disabled={billingSaving}
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {billingSaving ? 'Kaydediliyor...' : 'Fatura Bilgilerini Kaydet'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Değerlendirme Yap
            </DialogTitle>
          </DialogHeader>
          
          {selectedBookingForReview && (
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold text-slate-900">{selectedBookingForReview.facility_name}</h4>
                <p className="text-sm text-slate-600">
                  {format(new Date(selectedBookingForReview.date), 'd MMMM yyyy', { locale: tr })} - {selectedBookingForReview.start_time}
                </p>
              </div>

              {/* Star Rating */}
              <div className="space-y-2">
                <Label>Puanınız</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewRating(star)}
                      className="p-1 focus:outline-none"
                    >
                      <Star 
                        className={`w-8 h-8 transition-colors ${
                          star <= reviewRating 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500">
                  {reviewRating === 1 && 'Çok Kötü'}
                  {reviewRating === 2 && 'Kötü'}
                  {reviewRating === 3 && 'Orta'}
                  {reviewRating === 4 && 'İyi'}
                  {reviewRating === 5 && 'Mükemmel'}
                </p>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label>Yorumunuz</Label>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Deneyiminizi paylaşın... (en az 10 karakter)"
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={submitReview} 
              disabled={reviewLoading}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {reviewLoading ? 'Gönderiliyor...' : 'Gönder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Şifre Değiştir
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Mevcut Şifre</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="En az 8 karakter"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password-confirm">Yeni Şifre (Tekrar)</Label>
              <Input
                id="new-password-confirm"
                type={showNewPassword ? 'text' : 'password'}
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                required
                placeholder="Yeni şifrenizi tekrar girin"
              />
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordDialog(false)}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={passwordLoading}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                {passwordLoading ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function BookingList({ bookings, onCancel, navigate, getStatusBadge, showCancel, userReviews, onReview, showReview }) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        Bu kategoride rezervasyon bulunmuyor.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookings.map((booking) => {
        const hasReview = userReviews && userReviews[booking.booking_id];
        
        return (
          <div 
            key={booking.booking_id}
            className="border border-slate-200 rounded-xl p-4 hover:border-emerald-200 transition-colors"
            data-testid={`booking-item-${booking.booking_id}`}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-slate-900">{booking.facility_name}</h3>
                  {getStatusBadge(booking.payment_status)}
                  {hasReview && (
                    <Badge className="bg-yellow-100 text-yellow-700 flex items-center gap-1">
                      <Star className="w-3 h-3 fill-yellow-500" />
                      {hasReview.rating}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(booking.date), 'd MMMM yyyy', { locale: tr })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {booking.start_time} - {booking.end_time}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Kod: <code className="bg-slate-100 px-1 rounded">{booking.booking_code}</code>
                </p>
                {hasReview && (
                  <p className="text-sm text-slate-500 mt-2 italic">
                    "{hasReview.comment.substring(0, 100)}{hasReview.comment.length > 100 ? '...' : ''}"
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-emerald-700">
                  {booking.brut_tutar.toFixed(2)} ₺
                </span>
                
                {booking.payment_status === 'PENDING_PAYMENT' && (
                  <Button 
                    size="sm"
                    onClick={() => navigate(`/rezervasyon/${booking.booking_id}`)}
                    className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid={`pay-btn-${booking.booking_id}`}
                  >
                    Öde
                  </Button>
                )}
                
                {showCancel && booking.payment_status !== 'CANCELLED' && (
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => onCancel(booking.booking_id)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`cancel-btn-${booking.booking_id}`}
                  >
                    İptal
                  </Button>
                )}

                {showReview && booking.payment_status === 'CONFIRMED' && !hasReview && (
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => onReview(booking)}
                    className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Yorum Yap
                  </Button>
                )}

                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/rezervasyon/${booking.booking_id}`)}
                  data-testid={`view-btn-${booking.booking_id}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserDashboard;
